import React from "react";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import styles from "./styles.module.scss";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import AvailabilityWithDates from "app/routes/Scheduling/Availability/AvailabilityWithDates";
import { useTranslation } from "react-i18next";
import CloseIcon from "../../../../../../public/images/close.svg";
import { MinimalBookingOption } from 'app/routes/Scheduling/Availability/BookingOptionSelector';
import ButtonVariant from "app/components/Button/ButtonVariant";
import TextInput from "app/components/inputs/TextInput";

interface DailyTimeTable {
  day: number;
  active: boolean;
  startTime: string;
  endTime: string;
  value: number;
  label: string;
  short: string;
}

interface TimeTable {
  id?: string;
  name: string;
  dailyTimeTables: DailyTimeTable[];
  customPeriod: boolean;
  startDate: string | Date;
  endDate: string | Date;
  lessonRates?: MinimalBookingOption[] | null;
}

interface AddPricingProps {
  isOpen: boolean;
  onClose: () => void;
  onAddAvailability: (newAvailability: TimeTable) => void;
  schedule: TimeTable;
  setSchedule: (schedule: TimeTable) => void;
  bookingOptions?: MinimalBookingOption[] | null;
}

export default function AddAvailability({ bookingOptions, isOpen, onClose, onAddAvailability, schedule, setSchedule }: AddPricingProps) {
  const { t } = useTranslation();
  const {  formState: { errors } } = useForm<TimeTable>();

  const setScheduleData = (key: string, value: any) => {
    const updatedSchedule = {
      ...schedule,
      [key]: value,
    };

    setSchedule(updatedSchedule);
  };

  const onSubmit: SubmitHandler<TimeTable> = (data: TimeTable) => {
    onAddAvailability(data);
    onClose();
  };

  const methods = useForm({
    defaultValues: schedule,
    mode: 'onChange',
  });

  const {
    handleSubmit,
  } = methods;


  const cancelButton = (
    <ButtonVariant
      title={t('Cancel')}
      buttonType='secondary-outline'
      onClickFunc={onClose}
      buttonStyles={styles.cancel_button}
      childStyles={styles.button_text}
      data-testid="add-schedule-cancel-button"
    />
  );

  const confirmButton = (
    <ButtonVariant
      title={t('Create Schedule')}
      buttonType='primary'
      onClickFunc={handleSubmit(() => onSubmit(schedule))}
      buttonStyles={styles.confirm_button}
      childStyles={styles.button_text}
      data-testid="add-schedule-save-button"
    />
  );

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} classNames={styles.modal} data-testid="add-schedule-modal">
      <div className={styles.modal_header}>
        <h2>{t("Add Schedule")}</h2>
        <img className={styles.modal_status_icon} src={CloseIcon} alt="info" onClick={onClose} />
      </div>
        <div className={styles.modal_content}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(() => onSubmit(schedule))}>
              <div className={styles.form_row}>
                <TextInput
                  label={t("Schedule Name *")}
                  onChange={(e) => setScheduleData('name', e.target.value)}
                  name="schedule"
                  required
                  data-testid="schedule-name-input"
                />
                {errors.name && <span>{t("This field is required")}</span>}
              </div>
              <AvailabilityWithDates
                schedule={schedule}
                setScheduleData={setScheduleData}
                bookingOptions={bookingOptions}
              />
              <div className={styles.footer}>
                <div className={styles.buttons_container}>
                  {cancelButton} {confirmButton}
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
    </ModalLayout>
  );
}
